import { DOSSIERS_OPEN_SEARCH_SLICE_NAME, useFormaliteService } from '@europrocurement/l2d-domain';
import { type DossierOpenSearchJsonldOpensearchDossierStatutRead } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { useSnackbar } from 'notistack';
import React, { useEffect, useMemo, useState } from 'react';
import { Box, useTheme } from '@mui/material';
import { Accordion, ReactGridContainer } from '@europrocurement/flexy-components';
import { dateToISOYmd } from '@europrocurement/l2d-utils';
import { type NavigateFunction } from 'react-router';
import { useDispatch } from 'react-redux';
import { type ResizeHandleAxis } from 'react-grid-layout';
import { DOSSIER_STATUS_ARCHIVED } from '@europrocurement/l2d-modules/modules/Dossiers';
import { type UtilisateursJsonldUserworkgroupRead } from '@europrocurement/l2d-domain/openApi/ApiUtilisateur';
import FiltersDashboard from '../../components/FiltersDashboard/FiltersDashboard';
import DossierStatusWidgetDashboard from '../../components/DossierStatusWidgetDashboard/DossierStatusWidgetDashboard';
import TasksAndChartWidgetDashboard from '../../components/TasksAndChartWidgetDashboard/TasksAndChartWidgetDashboard';
import ValidatorsStatusWrapperWidgetDashboard from '../../components/ValidatorsStatusWrapperWidgetDashboard/ValidatorsStatusWrapperWidgetDashboard';
import NotificationWidgetDashboard from '../../components/NotificationWidgetDashboard/NotificationWidgetDashboard';

const COLUMN_RESIZE_HANDLES: ResizeHandleAxis[] = ['e', 'w'];

const OUTER_DASHBOARD_LAYOUT = [
    {
        i: 'widget-dossier-status',
        x: 0,
        y: 0,
        w: 6,
        h: 50,
        minW: 4,
        isResizable: true,
        resizeHandles: COLUMN_RESIZE_HANDLES,
    },
    {
        i: 'widget-greffes-status',
        x: 6,
        y: 0,
        w: 6,
        h: 50,
        minW: 4,
        isResizable: true,
        resizeHandles: COLUMN_RESIZE_HANDLES,
    },
    {
        i: 'widget-tasks-chart',
        x: 0,
        y: 4,
        w: 6,
        h: 100,
        minW: 2,
        isResizable: true,
        resizeHandles: COLUMN_RESIZE_HANDLES,
    },
    {
        i: 'list-messages-user',
        x: 6,
        y: 4,
        w: 6,
        h: 100,
        minW: 2,
        isResizable: true,
        resizeHandles: COLUMN_RESIZE_HANDLES,
    },
];

type FormalistDashboardProps = {
    title: string;
    formalistIds: number[];
    usersInGroups: UtilisateursJsonldUserworkgroupRead[];
    listeDossiersPath: string;
    navigate: NavigateFunction;
    formalistName?: string;
};

export type DashboardFiltersType = {
    startDate: string;
    endDate?: string;
    formalistIds: number[];
};

const FormalistDashboard: React.FunctionComponent<FormalistDashboardProps> = function ({
    title,
    formalistIds,
    usersInGroups,
    listeDossiersPath,
    navigate,
    formalistName,
}) {
    const { palette } = useTheme();
    const { dossierOpenSearchApi } = useFormaliteService();
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useDispatch();

    const [statusDossierOpenSearch, setStatusDossierOpenSearch] =
        useState<DossierOpenSearchJsonldOpensearchDossierStatutRead[]>();
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const initialFormalistIds = formalistIds;

    const [updatableFormalistIds, setUpdatableFormalistIds] = useState<number[]>(formalistIds);

    // Filters
    const initialStartDate = useMemo(() => {
        const date = new Date(2024, 0, 2);
        return dateToISOYmd(date);
    }, []);

    const [filters, setFilters] = useState<DashboardFiltersType>({
        startDate: initialStartDate,
        endDate: undefined,
        formalistIds,
    });

    const handleDossierStatusClick = (statusId?: number | null) => {
        if (!statusId) {
            return;
        }
        dispatch({ type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/resetmainFilter` });
        // dispatch  dossier status to redux and open the dossier list
        dispatch({
            type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainFilter`,
            payload: { key: 'formalisteId', value: updatableFormalistIds },
        });
        dispatch({
            type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainFilter`,
            payload: { key: 'dateCreation', value: filters },
        });

        const matchedUser = usersInGroups.find((user) => user.id === updatableFormalistIds[0]);
        const displayLabel = matchedUser
            ? [matchedUser.prenom, matchedUser.nom].filter(Boolean).join(' ')
            : formalistName;

        dispatch({
            type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainFilterLabel`,
            payload: { key: 'formalisteId', label: displayLabel },
        });

        dispatch({
            type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainPreFilter`,
            payload: {
                filters: [
                    {
                        field: 'statutId',
                        value: statusId,
                    },
                ],
            },
        });

        navigate(`/${listeDossiersPath}`);
    };

    const fetchDossierStatus = async () => {
        setIsSubmitting(true);
        const dossierStatusResponse = await dossierOpenSearchApi.apiOpensearchStatusesGetCollection(
            {
                formalisteId: updatableFormalistIds,
                itemsPerPage: 20,
                startDate: filters.startDate,
                endDate: filters.endDate,
            },
        );

        setIsSubmitting(false);
        if (dossierStatusResponse.status === 200) {
            return dossierStatusResponse.data['hydra:member'];
        }
        enqueueSnackbar('Une erreur est survenue lors de la récupération des dossiers', {
            variant: 'error',
        });
        return [];
    };

    // sync updatableFormalistIds when the filter dropdown changes formalists
    useEffect(() => {
        const hasChanged =
            filters.formalistIds.length !== updatableFormalistIds.length ||
            filters.formalistIds.some((id, i) => id !== updatableFormalistIds[i]);
        if (hasChanged) {
            setUpdatableFormalistIds(filters.formalistIds);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.formalistIds]);

    // refetch dossierStatus on filter or formalistId update
    useEffect(() => {
        fetchDossierStatus().then((res) =>
            setStatusDossierOpenSearch(
                res.filter(
                    (statutDossier) =>
                        ![DOSSIER_STATUS_ARCHIVED].includes(statutDossier.id as number),
                ),
            ),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updatableFormalistIds, filters.startDate, filters.endDate]);

    const tools = (
        <FiltersDashboard
            isSubmitting={isSubmitting}
            setFilters={setFilters}
            initialStartDate={initialStartDate}
            formalists={usersInGroups}
            initialFormalistIds={initialFormalistIds}
        />
    );

    const columnSx = {
        maxHeight: 'calc(100vh + 120px)',
        overflowY: 'auto',
        overflowX: 'hidden',
        position: 'relative',
        '&:hover .outer-column-drag-handle': { opacity: 1 },
    } as const;

    const dragHandleSx = {
        cursor: 'grab',
        height: '20px',
        width: '100%',
        opacity: 0,
        transition: 'opacity 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 0.5,
        '&::before': {
            content: '"⠿ ⠿"',
            fontSize: '14px',
            color: palette.grey[400],
            letterSpacing: '4px',
        },
    } as const;

    return (
        <Accordion
            title={title}
            expandLabel={tools}
            expanded
            expandColor={palette.secondary}
            sx={{ boxShadow: 'unset', border: 'none' }}
            disableRipple
        >
            <Box sx={{ maxHeight: 'calc(100vh + 100px)', overflowY: 'auto', overflowX: 'hidden' }}>
                <ReactGridContainer
                    localStorageName="grid-formalist-dashboard-outer"
                    layoutProps={OUTER_DASHBOARD_LAYOUT}
                    gridconfig={{ cols: 12, rowHeight: 10 }}
                    dragConfig={{ handle: '.outer-column-drag-handle' }}
                >
                    <Box
                        key="widget-dossier-status"
                        sx={columnSx}
                    >
                        <Box
                            className="outer-column-drag-handle"
                            sx={dragHandleSx}
                        />
                        {statusDossierOpenSearch && (
                            <DossierStatusWidgetDashboard
                                statusDossierOpenSearch={statusDossierOpenSearch}
                                handleDossierStatusClick={handleDossierStatusClick}
                            />
                        )}
                    </Box>
                    <Box
                        key="widget-greffes-status"
                        sx={columnSx}
                    >
                        <Box
                            className="outer-column-drag-handle"
                            sx={dragHandleSx}
                        />
                        <ValidatorsStatusWrapperWidgetDashboard
                            formalistIds={updatableFormalistIds}
                            filters={filters}
                            listeDossiersPath={listeDossiersPath}
                            localStorageName="grid-status-validators-formalist"
                            formalistName={formalistName}
                            showResetButton
                        />
                    </Box>
                    <Box
                        key="widget-tasks-chart"
                        sx={columnSx}
                    >
                        <Box
                            className="outer-column-drag-handle"
                            sx={dragHandleSx}
                        />
                        <TasksAndChartWidgetDashboard
                            statusDossierOpenSearch={statusDossierOpenSearch}
                            formalistIds={updatableFormalistIds}
                        />
                    </Box>
                    <Box
                        key="list-messages-user"
                        sx={{
                            height: '100%',
                            borderRadius: '8px',
                            padding: '0px 8px',
                            overflow: 'auto',
                        }}
                    >
                        <NotificationWidgetDashboard />
                    </Box>
                </ReactGridContainer>
            </Box>
        </Accordion>
    );
};

export default FormalistDashboard;
